define([
    '../Util/defaultValue',
    '../Util/defined',
    '../Util/encode',
    '../Util/callbackSystem',
    '../AI/Path',
    '../Enum/EnumPathPlayMode',
    '../Enum/EnumPathPlayStatus',
    '../System/Vec3Obj'
], function (
    defaultValue,
    defined,
    encode,
    callbackSystem,
    Path,
    EnumPathPlayMode,
    EnumPathPlayStatus,
    Vec3Obj
) {
    'use strict'

    /**
     * 人工模型。
     * 
     * @demo {@link http://lbs.zjkelan.com/Model.html|CooWasm Model demo}
     * @alias Model
     * @constructor
     *
     * @param {Object} props 包含如下属性的Object：
     * @param {String} props.url 模型路径。
     * @param {Vec3Obj} [props.translation=new Vec3Obj(0.0, 0.0, 0.0)] x、y、z轴偏移值。
     * @param {Vec3Obj} [props.scale=new Vec3Obj(1.0, 1.0, 1.0)] x、y、z轴比例值。
     * @param {Vec3Obj} [props.rotation=new Vec3Obj(0.0, 0.0, 0.0)] x、y、z轴旋转值。
     * @param {Vec3Obj} [props.location=null] 地理位置坐标值，默认为当前视点目标点位置。
     * @param {Boolean} [props.bVisible=true] 显隐状态。
     * @param {EnumPathPlayStatus} [props.playStatus=EnumPathPlayStatus.STOP] 运动状态。
     * @param {EnumPathPlayMode} [props.playMode=EnumPathPlayMode.ONCE] 运动模式。
     * @param {Path} [props.path=null] 绑定的路径对象。
     * @param {Number} [props.moveSpeed=5.0] 移动速度，单位：m/s。
     * @param {Number} [props.rotateSpeed=40.0] 转弯速度，单位：角度/s。
     * @param {Number} [props.interval=10] 间隔帧数，用于控制positionCallback触发间隔。
     * @param {Number} [props.minAltitude=0.0] 最小可见高度。
     * @param {Number} [props.maxAltitude=10000.0] 最大可见高度。
     * @param {CallbackBoolean} [callback=null] 加载成功后触发的函数回调。
     * @param {CallbackString} [wayPointCallback=null] 沿着路径运动时，经过关键路径点时，返回索引。
     * @param {CallbackString} [positionCallback=null] 沿着路径运动时，按照一定时间间隔返回经纬度地理坐标。
     * @example
     * var model = new CooWasm.Model({},(state)=>{console.log("load status:" + state)})
     */
    class Model {
        /**
         * @description 人工模型路径。
         * @type {String}
         */
        url
        /**
         * @description 人工模型x、y、z轴偏移值。
         * @type {Vec3Obj}
         */
        translation
        /**
         * @description 人工模型x、y、z轴比例值。
         * @type {Vec3Obj}
         */
        scale
        /**
         * @description 人工模型x、y、z轴旋转值。
         * @type {Vec3Obj}
         */
        rotation
        /**
         * @description 人工模型地理位置坐标值，默认为当前视点目标点位置。
         * @type {Vec3Obj}
         */
        location
        /**
         * @description 人工模型地理位置坐标参考系。
         * @type {String}
         * @readonly
         */
        spatialReference = 'GEOGCS["unknown",DATUM["WGS_1984",SPHEROID["wgs 84",6378137,298.257223563,AUTHORITY["epsg","7030"]],AUTHORITY["EPSG","6326"]],PRIMEM["greenwich",0,AUTHORITY["epsg","8901"]],UNIT["degree",0.0174532925199433,AUTHORITY["epsg","9122"]],AXIS["Longitude",EAST],AXIS["Latitude",NORTH]]'
        /**
         * @description 人工模型显隐状态。
         * @type {Boolean}
         * @default true
         */
        bVisible = true
        /**
         * @description 物体运动状态。
         * @type {EnumPlayStatus}
         */
        playStatus
        /**
         * @description 物体运动模式。
         * @type {EnumPathPlayMode}
         */
        playMode
        /**
         * @description 移动速度，单位：m/s。
         * @type {Number}
         */
        moveSpeed
        /**
         * @description 转弯速度，单位：角度/s。
         * @type {Number}
         */
        rotateSpeed
        /**
         * @description 间隔帧数，用于控制positionCallback触发间隔。
         * @type {Number}
         */
        interval
        /**
         * @description 最小可见高度。
         * @type {Number}
         */
        minAltitude
        /**
         * @description 最大可见高度。
         * @type {Number}
         */
        maxAltitude
        /**
         * @description 索引回调。
         * @type {CallbackString}
         */
        bIndexCallback
        /**
         * @description 位置回调。
         * @type {CallbackString}
         */
        bPositionCallback
        /**
         * @description 行为树内容。
         * @type {String}
         * @readonly
         */
        treeInfo = ""
        /**
         * @description 人工模型对象uuid。
         * @type {String}
         * @readonly
         */
        avatarUuid = null
        /**
         * @description 人工模型组件uuid。
         * @type {String}
         * @readonly
         */
        componentUuid = null
        /**
         * @description path对象。
         * @type {Path}
         */
        path = null
        /**
         * @description path对象的avatar uuid。
         * @type {String}
         * @readonly
         */
        pathAvatarUuid = null
        /**
         * @description path对象的component uuid。
         * @type {String}
         * @readonly
         */
        pathComponentUuid = null
        /**
         * @description GeoCuboid在CooWasm中类名。
         * @private
         * @readonly
         */
        className = "Model"
        static avatarType = "deSMP.Avatar.GeoObjectProxy"
        static componentType = "deSMP.AvatarComponent.v"

        constructor(props, callback, wayPointCallback, positionCallback) {
            if (!defined(props)) {
                props = {}
            }
            if (defined(props.build) && props.build == true) {
                return
            }
            if (!defined(props.url)) {
                console.log("need url!")
                return
            }
            this.url = props.url
            this.translation = defaultValue(props.translation, new Vec3Obj(0.0, 0.0, 0.0))
            this.scale = defaultValue(props.scale, new Vec3Obj(1.0, 1.0, 1.0))
            this.rotation = defaultValue(props.rotation, new Vec3Obj(0.0, 0.0, 0.0))
            this.location = defaultValue(props.location, null)
            this.bVisible = defaultValue(props.bVisible, true)
            this.playStatus = defaultValue(props.playStatus, EnumPathPlayStatus.STOP)
            this.playMode = defaultValue(props.playMode, EnumPathPlayMode.ONCE)
            this.path = defaultValue(props.path, null)
            this.moveSpeed = defaultValue(props.moveSpeed, 5.0)
            this.rotateSpeed = defaultValue(props.rotateSpeed, 40.0)
            this.interval = defaultValue(props.interval, 10)
            let avatarProps = ""
            let componentProps = ""
            avatarProps += this.toStr("bVisible", props.bVisible, "Visible")
            avatarProps += this.toStr("translation", props.translation, "Translation")
            avatarProps += this.toStr("scale", props.scale, "Scale")
            avatarProps += this.toStr("rotation", props.rotation, "Rotation")
            if (defined(this.location)) {
                avatarProps += "Location:" + this.spatialReference + "/" + this.location.toStr() + "|"
            }
            //如有路径相应信息，则创建action tree
            if (defined(this.path)) {
                this.bIndexCallback = wayPointCallback
                this.bPositionCallback = positionCallback
                this.treeInfo = this.toXml()
                componentProps += "BehaviorTreeInfo" + ":" + this.treeInfo + "|"
                componentProps += this.toStr("playStatus", this.playStatus, "BehaviorTreePlayState")
            }

            if (this.location !== null) {
                var idInfo = Element.prototype.NativeAPI.LoadModelWithAI(encode(this.url), encode(avatarProps), encode(componentProps), defined(callback))
                if (idInfo != "") {
                    var idArray = idInfo.split('|')
                    this.avatarUuid = idArray[0]
                    this.componentUuid = idArray[1]
                    if (!defined(callback)) {
                        Element.prototype.Map.models.push(this)
                    }
                    callbackSystem.register("LoadAvatarAndComponent", this.avatarUuid, (state) => {
                        this.getProperties()
                        if (defined(callback)) {
                            Element.prototype.Map.models.push(this)
                            callback(state)
                        }
                    })
                    callbackSystem.register("WayPoint", this.avatarUuid, (index) => {
                        if (defined(wayPointCallback)) {
                            wayPointCallback(index)
                        }
                    })
                    callbackSystem.register("CurrentPosition", this.avatarUuid, (position) => {
                        if (defined(wayPointCallback)) {
                            positionCallback(position)
                        }
                    })
                }
            } else {
                var idInfo = Element.prototype.NativeAPI.LoadModelWithMouseAI(encode(this.url), encode(avatarProps), encode(componentProps))
                if (idInfo != "") {
                    var idArray = idInfo.split('|')
                    this.avatarUuid = idArray[0]
                    this.componentUuid = idArray[1]
                    if (!defined(callback)) {
                        Element.prototype.Map.models.push(this)
                    }
                    callbackSystem.register("LoadAvatarAndComponent", this.avatarUuid, (state) => {
                        this.getProperties()
                        if (defined(callback)) {
                            Element.prototype.Map.models.push(this)
                            callback(state)
                        }
                    })
                    callbackSystem.register("WayPoint", this.avatarUuid, (index) => {
                        if (defined(wayPointCallback)) {
                            wayPointCallback(index)
                        }
                    })
                    callbackSystem.register("CurrentPosition", this.avatarUuid, (position) => {
                        if (defined(wayPointCallback)) {
                            positionCallback(position)
                        }
                    })
                }
            }
        }

        /**
         * <p>
         * 更新单个或者多个对象属性，使之立即生效。
         * </p>
         *
         * @param {Object} [props] 包含如下属性的Object。
         * @param {Vec3Obj} [props.translation] x、y、z轴偏移值。
         * @param {Vec3Obj} [props.scale] x、y、z轴比例值。
         * @param {Vec3Obj} [props.rotation] x、y、z轴旋转值。
         * @param {Vec3Obj} [props.location] 地理位置坐标值，默认为当前视点目标点位置。
         * @param {Boolean} [props.bVisible] 显隐状态。
         * @param {EnumPathPlayStatus} [props.playStatus] 运动状态。
         * @param {EnumPathPlayMode} [props.mode] 运动模式。
         * @param {Path} [props.path] 绑定的路径对象。
         * @param {Number} [props.moveSpeed] 移动速度，单位：m/s。
         * @param {Number} [props.rotateSpeed] 转弯速度，单位：角度/s。
         * @param {Number} [props.interval] 间隔帧数，用于控制positionCallback触发间隔。
         * @param {CallbackBoolean} [callback=null] 更新成功后触发的函数回调。
         * @param {CallbackString} [wayPointCallback=null] 沿着路径运动时，经过关键路径点时，返回索引。
         * @param {CallbackString} [positionCallback=null] 沿着路径运动时，按照一定时间间隔返回经纬度地理坐标。
         * @example
         * model.update({
         *    bVisible:false
         * },(state)=>{console.log("update status:" + state)})
         */
        update(props, callback, wayPointCallback, positionCallback) {
            let avatarProps = ""
            let componentProps = ""
            if (defined(wayPointCallback) && wayPointCallback !== this.bIndexCallback) {
                this.bIndexCallback = wayPointCallback
            }
            if (defined(positionCallback) && positionCallback !== this.bPositionCallback) {
                this.bPositionCallback = positionCallback
            }
            if (defined(props)) {
                avatarProps += this.toStr("translation", props.translation, "Translation")
                avatarProps += this.toStr("scale", props.scale, "Scale")
                avatarProps += this.toStr("rotation", props.rotation, "Rotation")
                avatarProps += this.toStr("bVisible", props.bVisible, "Visible")
                if (defined(props.location)) {
                    this.location = props.location
                    avatarProps += "Location:" + this.spatialReference + "/" + this.location.toStr() + "|"
                }
                
                //ai
                if(!defined(this.path) && defined(props.path)){
                    this.path = props.path
                    let needToUpdateTree = true
                    if (defined(props.moveSpeed) && props.moveSpeed !== this.moveSpeed) {
                        this.moveSpeed = props.moveSpeed
                    }
                    if (defined(props.rotateSpeed) && props.rotateSpeed !== this.rotateSpeed) {
                        this.rotateSpeed = props.rotateSpeed
                    }
                    if (defined(props.interval) && props.interval !== this.interval) {
                        this.interval = props.interval
                    }
                    if (defined(props.playMode) && props.playMode !== this.playMode) {
                        this.playMode = props.playMode
                    }
                    componentProps += "BehaviorTreeInfo" + ":" + this.toXml() + "|"
                    // let treeInfo = needToUpdateTree? this.toXml() : this.treeInfo
                    // if (needToUpdateTree) {
                    //     componentProps += "BehaviorTreeInfo" + ":" + treeInfo + "|"
                    // }
                }else if(defined(props.path)){
                    let needToUpdateTree = true
                    if (defined(props.moveSpeed) && props.moveSpeed !== this.moveSpeed) {
                        this.moveSpeed = props.moveSpeed
                        needToUpdateTree = true
                    }
                    if (defined(props.rotateSpeed) && props.rotateSpeed !== this.rotateSpeed) {
                        this.rotateSpeed = props.rotateSpeed
                        needToUpdateTree = true
                    }
                    if (defined(props.interval) && props.interval !== this.interval) {
                        this.interval = props.interval
                        needToUpdateTree = true
                    }
                    if (defined(props.playMode) && props.playMode !== this.playMode) {
                        this.playMode = props.playMode
                        needToUpdateTree = true
                    }
                    let treeInfo = needToUpdateTree? this.toXml() : this.treeInfo
                    if (needToUpdateTree) {
                        componentProps += "BehaviorTreeInfo" + ":" + treeInfo + "|"
                    }
                }
                // this.path = defaultValue(props.path, this.path)
                // if (defined(this.path)) {
                //     let needToUpdateTree = false
                //     // Check if any properties that affect the XML have changed
                //     if (defined(props.moveSpeed) && props.moveSpeed !== this.moveSpeed) {
                //         this.moveSpeed = props.moveSpeed
                //         needToUpdateTree = true
                //     }
                //     if (defined(props.rotateSpeed) && props.rotateSpeed !== this.rotateSpeed) {
                //         this.rotateSpeed = props.rotateSpeed
                //         needToUpdateTree = true
                //     }
                //     if (defined(props.interval) && props.interval !== this.interval) {
                //         this.interval = props.interval
                //         needToUpdateTree = true
                //     }
                //     if (defined(props.playMode) && props.playMode !== this.playMode) {
                //         this.playMode = props.playMode
                //         needToUpdateTree = true
                //     }
                //     let treeInfo = needToUpdateTree? this.toXml() : this.treeInfo
                //     if (needToUpdateTree) {
                //         componentProps += "BehaviorTreeInfo" + ":" + treeInfo + "|"
                //     }
                // }
                componentProps += this.toStr("playStatus", props.playStatus, "BehaviorTreePlayState")
                
                callbackSystem.register("WayPoint", this.avatarUuid, (index) => {
                    if (defined(this.bIndexCallback)) {
                        this.bIndexCallback(index)
                    }
                })
                callbackSystem.register("CurrentPosition", this.avatarUuid, (position) => {
                    if (defined(this.bPositionCallback)) {
                        this.bPositionCallback(position)
                    }
                })
                callbackSystem.register("UpdateAvatarAndComponent", this.avatarUuid, (state) => {
                    this.getProperties()
                    if (defined(callback)) {
                        callback(state)
                    }
                })
                Element.prototype.NativeAPI.UpdateAvatarAndComponent(encode(this.avatarUuid), encode(this.componentUuid), encode(avatarProps), encode(componentProps), defined(callback))
            }
        }
        toStr(memName, value, key) {
            let result = ""
            if (defined(value)) {
                this[memName] = value
                if (typeof value.toStr === 'function') {
                    result = key + ":" + value.toStr() + "|"
                } else {
                    result = key + ":" + value + "|"
                }
            }
            return result
        }
        /**
         * 生成符合RFC4122标准的UUID (版本4)
         * @returns {String} 返回格式为'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'的UUID
         */
        generateUuid() {
            return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
                const r = Math.random() * 16 | 0
                const v = c === 'x' ? r : (r & 0x3 | 0x8)
                return v.toString(16)
            })
        }

        toXml() {
            if (!defined(this.path)) {
                console.log("need Path, action tree create failed!")
                return ""
            }
            let behaviorTreeId = this.generateUuid()
            let sequenceId = this.generateUuid()
            let wayPointActionId = this.generateUuid()
            let moveActionId = this.generateUuid()
            let treeInfo =
                `<?xml version="1.0" encoding="UTF-8" standalone="no" ?>
                <behaviorTrees>
                    <behaviorTree id="${behaviorTreeId}" name="behavior" root="true">
                        <sequence id="${sequenceId}">
                        <wayPointAction id="${wayPointActionId}" pathComID="${this.path.avatarUuid}/${this.path.componentUuid}" repeatType="${this.playMode}" arrivalCallback="${defined(this.bIndexCallback)}" />
                        <bezierMoveAction id="${moveActionId}" moveTargetType="2" moveSpeed="${this.moveSpeed}" faceTargetType="2" customPitch="0.0" customAzimuth="0.0" turnSpeed="${this.rotateSpeed}" callbackInterval="${this.interval}" />
                        </sequence>
                    </behaviorTree>
                </behaviorTrees>`
            return treeInfo
        }
        /**
         * 从XML中提取CDATA内容
         * @param {String} xmlString 包含CDATA部分的XML字符串
         * @returns {String} CDATA内容，如果没有找到则返回空字符串
         */
        extractCDATAContent(xmlString) {
            // 匹配CDATA部分的正则表达式
            const cdataRegex = /<!\[CDATA\[([\s\S]*?)\]\]>/g

            const matches = cdataRegex.exec(xmlString)
            if (matches && matches.length > 1) {
                return matches[1]
            }

            return ''
        }

        fromXml(xml) {
            if (!defined(xml) || xml.trim() === "") {
                console.warn("Invalid XML provided to fromXml")
                return
            }
            xml = this.extractCDATAContent(xml)
            try {
                // 创建XML文档对象
                let xmlDoc = null
                if (window.DOMParser) {
                    const parser = new DOMParser()
                    xmlDoc = parser.parseFromString(xml, "text/xml")
                    // 检查解析错误
                    const parseError = xmlDoc.getElementsByTagName("parsererror")
                    if (parseError.length > 0) {
                        console.log("XML parsing error: " + parseError[0].textContent)
                    }
                } else {
                    xmlDoc = new ActiveXObject("Microsoft.XMLDOM")
                    xmlDoc.async = false
                    xmlDoc.loadXML(xml)

                    if (xmlDoc.parseError.errorCode !== 0) {
                        console.log("XML parsing error: " + xmlDoc.parseError.reason)
                    }
                }
                // 提取所需元素
                const behaviorTrees = xmlDoc.getElementsByTagName("behaviorTrees")[0]
                if (!behaviorTrees) console.log("Missing behaviorTrees element")
                const behaviorTree = behaviorTrees.getElementsByTagName("behaviorTree")[0]
                if (!behaviorTree) console.log("Missing behaviorTree element")
                const sequence = behaviorTree.getElementsByTagName("sequence")[0]
                if (!sequence) console.log("Missing sequence element")
                const wayPointAction = sequence.getElementsByTagName("wayPointAction")[0]
                const moveAction = sequence.getElementsByTagName("bezierMoveAction")[0]
                if (!wayPointAction || !moveAction) {
                    console.log("Missing required action elements")
                }
                // 获取playMode
                const repeatType = wayPointAction.getAttribute("repeatType")
                this.playMode = defined(repeatType) ? Number(repeatType) : this.playMode
                // 获取moveSpeed并确保是数值类型
                const moveSpeed = moveAction.getAttribute("moveSpeed")
                this.moveSpeed = defined(moveSpeed) ? parseFloat(moveSpeed) : this.moveSpeed
                // 获取rotateSpeed并确保是数值类型
                const turnSpeed = moveAction.getAttribute("turnSpeed")
                this.rotateSpeed = defined(turnSpeed) ? parseFloat(turnSpeed) : this.rotateSpeed
                // 获取interval并确保是数值类型
                const callbackInterval = moveAction.getAttribute("callbackInterval")
                this.interval = defined(callbackInterval) ? parseInt(callbackInterval, 10) : this.interval
                // 返回解析后的属性对象，方便调用者使用
                return {
                    playMode: this.playMode,
                    moveSpeed: this.moveSpeed,
                    rotateSpeed: this.rotateSpeed,
                    interval: this.interval
                }
            } catch (error) {
                console.error("Error parsing XML:", error)
                // 发生错误时返回undefined而不是部分解析的结果
                return undefined
            }
        }
        /**
         * 从场景中定位自身(自动根据数据包围盒定位，不可修改定位参数)。
         * @param {CallbackBoolean} [callback=null] 飞行结束后触发的函数回调。
         * @example
         * model.locate((state)=>{console.log("locate status:" + state)})
         */
        locate(callback) {
            callbackSystem.register("", "LocateAvatar", (state) => {
                if (defined(callback)) {
                    callback(state)
                }
            })
            Element.prototype.NativeAPI.LocateAvatar(encode(this.avatarUuid))
        }

        /**
         * 获取对象包含的属性。
         * @returns {Boolean} 是否获取成功。获取成功时，会自动赋值给属性。
         * @example
         * model.getProperties()
         */
        getProperties() {
            if (!defined(this.avatarUuid)) {
                console.log("uuid require!")
                return false
            }
            let pAvatarUuid = Element.prototype.NativeAPI.Uuid_New()
            Element.prototype.NativeAPI.Uuid_SetId(pAvatarUuid, encode(this.avatarUuid))
            let pAvatar = Element.prototype.NativeAPI.MapImpl_getAvatar(pAvatarUuid)
            let avatarPropSize = Element.prototype.NativeAPI.Avatar_GetPropertiesSize(pAvatar)
            for (let i = 0; i < avatarPropSize; ++i) {
                let pProp = Element.prototype.NativeAPI.Avatar_GetProperty(pAvatar, i)
                let name = Element.prototype.NativeAPI.Property_GetName(pProp)
                let value = Element.prototype.NativeAPI.Property_GetValue(pProp)
                if (name == "Visible") {
                    if (value == "true" || value == "1") {
                        this.bVisible = true
                    } else {
                        this.bVisible = false
                    }
                } else if (name == "Scale") {
                    this.scale = Vec3Obj.fromStr(value)
                } else if (name == "Rotation") {
                    this.rotation = Vec3Obj.fromStr(value)
                } else if (name == "Translation") {
                    this.translation = Vec3Obj.fromStr(value)
                } else if (name == "Location") {
                    if (value != "") {
                        let arr = value.split('/')
                        this.spatialReference = arr[0]
                        this.location = Vec3Obj.fromStr(arr[1])
                    }
                }
            }
            let avatarAcSize = Element.prototype.NativeAPI.Avatar_GetAvatarComponentsSize(pAvatar)
            for (let j = 0; j < avatarAcSize; ++j) {
                let pAc = Element.prototype.NativeAPI.Avatar_GetAvatarComponent(pAvatar, j)
                let propSize = Element.prototype.NativeAPI.AvatarComponent_GetPropertiesSize(pAc)
                let pUuid = Element.prototype.NativeAPI.AvatarComponent_GetUuid(pAc)
                let uuid = Element.prototype.NativeAPI.Uuid_GetId(pUuid)
                this.componentUuid = uuid
                for (let z = 0; z < propSize; ++z) {
                    let pProp = Element.prototype.NativeAPI.AvatarComponent_GetProperty(pAc, z)
                    let name = Element.prototype.NativeAPI.Property_GetName(pProp)
                    let value = Element.prototype.NativeAPI.Property_GetValue(pProp)
                    for (let z = 0; z < propSize; ++z) {
                        let pProp = Element.prototype.NativeAPI.AvatarComponent_GetProperty(pAc, z)
                        let name = Element.prototype.NativeAPI.Property_GetName(pProp)
                        let value = Element.prototype.NativeAPI.Property_GetValue(pProp)
                        if (name == "BehaviorTreeInfo") {
                            let treeInfo = this.fromXml(value)
                        } else if (name == "BehaviorTreePlayState") {
                            this.playStatus = Number(value)
                        }
                    }
                }
            }
            Element.prototype.NativeAPI.Avatar_Delete(pAvatar)
            Element.prototype.NativeAPI.Uuid_Delete(pAvatarUuid)
            return true
        }
        /**
         * 与路径绑定，将按照路径线运动。
         * @param {Path} path Path对象。
         * @param {Boolean} bPlay 是否播放。
         * @param {CallbackBoolean} [callback=null] 更新成功后触发的函数回调。
         * @param {CallbackString} [wayPointCallback=null] 沿着路径运动时，经过关键路径点时，返回索引。
         * @param {CallbackString} [positionCallback=null] 沿着路径运动时，按照一定时间间隔返回经纬度地理坐标。
         * @example
         * var path = new CooWasm.Path({
         *      points:[
         *          new CooWasm.Vec3Obj(120.14809914543076, 30.162721658047321, 12.651933761499823),
         *          new CooWasm.Vec3Obj(120.15027113314304, 30.163552581628441, 32.567664995975790),
         *          new CooWasm.Vec3Obj(120.15215840759309, 30.159521920633519, 10.357548876851798),
         *          new CooWasm.Vec3Obj(120.15043531969498, 30.158788468192203, 9.7634695854038007)
         *      ]
         * })
         * geoCuboid.bindPath(path,true)
         */
        bindPath(path, bPlay, callback, wayPointCallback, positionCallback) {
            if (!(path instanceof Path)) {
                throw new Error('param must be an instance of Path')
            }
            let status = 0
            if (bPlay) {
                status = EnumPathPlayStatus.PLAY
                //Element.prototype.NativeAPI.SetAvatarOrientation(encode(this.avatarUuid), path.points[0].x, path.points[0].y, path.points[0].z, path.points[1].x, path.points[1].y, path.points[1].z)
            } else {
                status = EnumPathPlayStatus.STOP
            }
            this.update({
                path: path,
                playStatus: status
            }, state => {
                if (defined(callback)) {
                    callback(state)
                }
            }, index => {
                if (defined(wayPointCallback)) {
                    wayPointCallback(index)
                }
            }, position => {
                if (defined(positionCallback)) {
                    positionCallback(position)
                }
            })
        }
        /**
         * 从场景中销毁自身。
         * @param {CallbackBoolean} [callback=null] 销毁对象后触发的函数回调。
         * @returns null 空值。
         * @example
         * model = model.destroy((state)=>{console.log("destroy status:" + state)})
         */
        destroy(callback) {
            if (!defined(this.avatarUuid)) {
                console.log("avatarUuid is null!")
                return null
            }
            callbackSystem.register("RemoveAvatar", this.avatarUuid, (state) => {
                if (defined(callback)) {
                    callback(state)
                }
                Element.prototype.Map.models = Element.prototype.Map.models.filter(item => item !== this)
                callbackSystem.remove("LoadAvatarAndComponent", this.avatarUuid)
                callbackSystem.remove("UpdateAvatarAndComponent", this.avatarUuid)
                callbackSystem.remove("WayPoint", this.avatarUuid)
                callbackSystem.remove("CurrentPosition", this.avatarUuid)
                callbackSystem.remove("", "LocateAvatar")
                callbackSystem.remove("RemoveAvatar", this.avatarUuid)
                this.avatarUuid = null
                this.componentUuid = null
            })
            Element.prototype.NativeAPI.RemoveAvatar(encode(this.avatarUuid))
            return null
        }

    }
    return Model
})